import { FC } from 'react';
import {
  Container,
  Link,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';

import Meta from '@bizapp-lp/pjb/organisms/Meta';
import { Terms } from '@bizapp-lp/pjb/templates/Terms';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    innerList: {
      '& > li': {
        listStyle: 'none',
        marginLeft: theme.spacing(-4),
      },
    },
    noteList: {
      margin: '8px 0 0 !important',
      padding: '0 !important',
      '& li': {
        position: 'static',
        listStyle: 'none',
        marginTop: '-13px !important',
        marginBottom: '0 !important',
        '&:before': {
          content: "'※'",
          position: 'relative',
          top: '21px',
          left: '-14px',
          fontSize: '14px',
          lineSpacing: '21px',
        },
      },
    },
    lineBreak: {
      '& span': {
        display: 'block',
      },
    },
  }),
);

const title =
  'Project Boardサービス仕様書 | 株式会社ワークスアプリケーションズ';
const description =
  '株式会社ワークスアプリケーションズがご提供する、Project Boardサービス仕様書です。';

const ServiceSpec: FC = () => {
  const classes = useStyles();

  const termsProps = {
    pageTitle: (
      <>
        <span style={{ display: 'block' }}>HUE Works Suite</span>
        <span style={{ display: 'block' }}>Project Boardサービス仕様書</span>
      </>
    ),
    headerContent: 'Ver.1.1.0',
  };
  return (
    <>
      <Meta>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Meta>
      <Terms {...termsProps}>
        <Container disableGutters maxWidth={false}>
          <Typography>
            これは過去のHUE Works Suite Project
            Boardサービス仕様書です。最新版は
            <Link href="https://www.worksap.co.jp/services/spec/pjb-spec/">
              こちら
            </Link>
            です。
          </Typography>
          <Typography variant="h2">1. 本書の定義</Typography>
          <Typography variant="body1">
            本書は、お客様におけるHUE Works Suite Project Board（以下、 Project
            Board）利用にあたり株式会社ワークスアプリケーションズ・エンタープライズ（以下、弊社）より提供可能なクラウドサービスの内容を記述したものです。Project
            Boardの利用に際しては、本内容および「HUE Works Suite / HUE Works
            Suite DX Solutions利用規約」に同意していただく必要があります。
          </Typography>
          <Typography variant="body1">
            本書と「HUE Works Suite / HUE Works Suite DX
            Solutions利用規約」の記載内容が異なる場合には利用規約の内容が優先されます。
          </Typography>
          <Typography variant="body1">
            過去に提示済みのサービス内容と内容が異なる場合には、常に最新のサービス内容が優先して適用されます。
          </Typography>
        </Container>

        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">2. Project Boardサービス概要</Typography>
          <Typography variant="body1">
            Project
            Boardはオンラインシェア型プロジェクト進捗管理サービスです。プロジェクト管理でよく利用されるガントチャート機能やかんばん機能を搭載し、チームや組織、外部ユーザーとのコラボレーションによる業務の効率化をサポートします。
          </Typography>
          <Typography variant="body1">
            ※本サービスは法人、団体又は個人のお客様に向けたサービスです。
          </Typography>
          <Typography variant="body1">
            ※本サービスでは外部ユーザーをゲストユーザーとして招待し、共同で利用することが可能です。ただし、当該外部ユーザーによるProject
            Board利用に関連する一切の行為はお客様による行為とみなすこととし、当該外部ユーザーによる利用規約および本サービス仕様書の違反についてはお客様による違反としてお客様が弊社に対して一切の責任を負うものとします。
          </Typography>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.1 標準サービス機能</Typography>
            <Typography variant="body1">
              Project
              Boardでは下記機能を提供いたします。あわせて、初期構築作業（利用環境構築作業および管理者アカウント作成作業）を提供いたします。
            </Typography>
            <Typography variant="body1">
              また、下記機能および初期構築作業の仕様、品質その他一切の内容に関する詳細は、弊社がその裁量によって決定するものとします。
            </Typography>
            <List component="ul">
              <ListItem>
                <ListItemText>管理機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>アカウント管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>ゲストアカウント管理</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>権限管理</ListItemText>
                  </ListItem>
                </List>
              </ListItem>
              <ListItem>
                <ListItemText>ユーザー機能</ListItemText>
                <List component="ul" className={classes.innerList}>
                  <ListItem>
                    <ListItemText>プロジェクト作成</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>ガントチャート</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>かんばん</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      コラボレーション（ユーザー招待、ゲスト招待、URL共有）
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>分析</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>フォルダ管理</ListItemText>
                  </ListItem>
                </List>
              </ListItem>
            </List>
            <Typography variant="body1">
              ※ゲストユーザーは招待されたプロジェクト内の機能のみを利用できます。権限管理にて管理者権限を付与することやプロジェクトの管理者になることはできません。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">
              2.2 Project Boardオプションサービス
            </Typography>
            <Typography variant="body1">
              Project
              Boardでは下記のオプションサービスを別途有償にて提供いたします。
            </Typography>
            <Typography variant="body1">
              また、下記のサービスの仕様、品質その他一切の内容に関する詳細は、弊社がその裁量によって決定するものとします。
            </Typography>
            <List component="ul">
              <ListItem>
                <ListItemText>
                  接続IP制御：本サービスへの接続元グローバルIPをCIDR形式で指定し制御できます
                </ListItemText>
              </ListItem>
            </List>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">
              2.3 環境構築およびアカウントの発行
            </Typography>
            <Typography variant="body1">
              お申し込みから1週間ほどでお申し込み時に登録いただいたメールアドレスあてに、アクセスURLと管理者アカウントの初期ログインIDをお送りいたします。2ユーザー目以降については、管理者アカウントにてお客様ご自身で登録いただけます。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.4 サポートサービス</Typography>
            <Typography variant="body1">
              お申し込みいただきましたメールアドレスあてに機能マニュアル、管理者マニュアルおよび問い合わせ先メールアドレスをお送りいたします。ご不明点がありましたら問い合わせ先メールアドレスまでお問い合わせください。3営業日をめどに１次回答いたします。
            </Typography>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.5 利用料金</Typography>
            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">【標準サービス】</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    月額利用料：1,056円(税込）/ライセンス
                  </ListItemText>
                  <List component="ul" className={classes.noteList}>
                    <ListItem>
                      <ListItemText>
                        初回月額利用料に初期構築(利用環境構築)費用を含みます。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        「契約ライセンス数×10GB」の容量制限があります。容量制限は全ユーザーで利用できる総量となります。
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
              </List>
              <Typography variant="body1">
                Project
                Boardライセンス数は、一般ユーザーのアカウント数、及び、ゲストユーザーのアカウント数の総数とします。
              </Typography>
              <Typography variant="body1">
                利用料金は、契約ライセンス数に対して発生します。ライセンスは、アカウントの状態によって自動付与され、アカウントの状態が有効の場合はライセンスが付与された状態となります。
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ライセンス</TableCell>
                      <TableCell>詳細</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        Project Boardライセンス
                      </TableCell>
                      <TableCell>
                        <span style={{ display: 'block' }}>
                          Project
                          Boardのアカウント管理機能に登録されているアカウントの中で、アカウントの状態が「有効」であるユーザーがライセンスの対象となります。
                        </span>
                        <span style={{ display: 'block' }}>
                          「有効」アカウント数が契約ライセンス数を超えない限り、アカウントの状態の変更は同一月内に何度でも行うことができます。
                        </span>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Container>

            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">【オプションサービス】</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    接続IP制御（登録・変更・削除）：本オプションサービス利用契約1件あたり、基本料金11,000円（税込）
                  </ListItemText>
                  <List component="ul" className={classes.noteList}>
                    <ListItem>
                      <ListItemText>
                        1件の本オプションサービス利用契約において、基本料金内にてIPアドレスの登録、変更、削除のいずれかの作業を10回までお申し込みいただけます。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        IPアドレスの登録、変更、削除のいずれかの作業の実施毎に1回と算定します（同一のIPアドレスに対する複数回の作業であっても実施した作業回数に応じて算定します）。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        1件の本オプションサービス利用契約において、作業を10回を超えてお申し込みいただきたい場合は、超過作業数に応じて、10回単位で11,000円（税込）が追加料金として加算されます。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        1件の本オプションサービス利用契約において申し込む作業が10回に満たない場合でも、料金の減額は行いません。また、その場合においても、次回以降の本オプションサービス利用契約時における基本料金や基本料金内にてお申し込み可能な作業回数に変更はありません。
                      </ListItemText>
                    </ListItem>
                  </List>
                  <ListItemText style={{ marginTop: '16px' }}>
                    【例】
                  </ListItemText>
                  <List component="ul" style={{ marginTop: 0 }}>
                    <ListItem>
                      <ListItemText>
                        新規でお客様が5個のIPアドレスの登録を申し込む場合、本オプションサービス利用契約が1件成立し、基本料金11,000円（税込）が発生します。さらに、当該お客様が、追加で5個のIPアドレスの登録を申し込む場合、または登録された5個のIPアドレスの削除を申し込む場合、いずれの場合であっても、別個の本オプションサービス利用契約が新たに1件成立し、改めて基本料金11,000円（税込）が発生します。
                      </ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText>
                        新規でお客様が15個のIPアドレスの登録を申し込む場合、本オプションサービス利用契約1件あたりの基本料金11,000円（税込）に、10回を超えるIPアドレス5個分の超過作業5回分に対する追加料金11,000円（税込）が加算され、合計22,000円（税込）が発生します。
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
              </List>
            </Container>
          </Container>

          <Container disableGutters maxWidth={false}>
            <Typography variant="h3">2.6 算定方法およびお支払い</Typography>

            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">算定方法</Typography>
              <List component="ul">
                <ListItem>
                  <ListItemText>
                    毎月末時点の契約ライセンス数に応じて翌月1日より1ヶ月分の費用が発生します
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    初月のご請求金額は、日割り計算となります
                  </ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>
                        初月のご請求金額 ＝{'{'}月額利用料 －（月額利用料 ×
                        利用しない日数 ÷ 暦日数）{'}'} × 契約ライセンス数
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    ライセンス追加のご請求金額は、日割り計算となります
                  </ListItemText>
                  <List component="ul" className={classes.innerList}>
                    <ListItem>
                      <ListItemText>
                        追加のご請求金額 ＝ {'{'}月額利用料 －（月額利用料 ×
                        利用しない日数 ÷ 暦日数）{'}'} × 追加契約ライセンス数
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    日割り計算は先乗後除で行い、小数点は四捨五入とします
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText>
                    契約期間の途中で利用契約が終了する場合におけるご請求金額は、日割り計算による減額又は返金は行わず、利用契約の終了日から当該終了日が属する月の末日までの費用も発生します
                  </ListItemText>
                </ListItem>
              </List>
            </Container>

            <Container disableGutters maxWidth={false}>
              <Typography variant="h4">お支払い</Typography>

              <Container disableGutters maxWidth={false}>
                <Typography variant="h5">【請求書支払】</Typography>
                <List component="ul">
                  <ListItem>
                    <ListItemText>毎月末締め、翌月末払い</ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      お支払いが発生する月の8営業日以内にお支払いのご案内をお送りします。
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  ※請求はマネーフォワードケッサイ株式会社による「マネーフォワード
                  ケッサイ」サービスにて行われます
                </Typography>
              </Container>

              <Container disableGutters maxWidth={false}>
                <Typography variant="h5">
                  【クレジットカード払い（請求書有）】
                </Typography>
                <List component="ul">
                  <ListItem>
                    <ListItemText>
                      初回のご請求金額は、クレジットカード払いの正式登録が完了すると同時に、初回の決済が行われます。
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      二回目のお支払い以降は、毎月1日にお支払いのご案内をお送りし、8日に決済が行われます。
                    </ListItemText>
                  </ListItem>
                  <ListItem>
                    <ListItemText>
                      ライセンス追加のご請求金額は、ライセンス追加登録が完了すると同時に、当月分の決済が行われ、翌月分以降は既存ライセンスと合算して請求及び決済が行われます。
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  ※請求はストライプジャパン株式会社（以下、ストライプ）による「Payments」（以下、Payments）サービスにて行われます。
                </Typography>
                <Typography variant="body1">
                  ※お客様は、当社がお客様を代理してPaymentsを通じてクレジットカード決済を行うこと、並びに、当該クレジットカード決済の処理はストライプ及びストライプの世界各国における関連会社が行うことについて同意するものとします。
                </Typography>
              </Container>
            </Container>
          </Container>
        </Container>
        <Container disableGutters maxWidth={false}>
          <Typography variant="h2">付則</Typography>
          <Typography>
            改正後の本仕様書は、2021年12月23日より効力を有するものとします。
          </Typography>
          <Typography className={classes.lineBreak}>
            <span>
              <Link
                component={RouterLink}
                to="/pjb/spec-archive/service-spec-01-20210713"
              >
                Version 1.0.1：2021年7月13日
              </Link>
            </span>
            <span>Version 1.1.0：2021年12月23日</span>
          </Typography>
        </Container>
      </Terms>
    </>
  );
};

export default ServiceSpec;
